/**
 * @description SA / CP catalog + flags editor (LYN-411)
 *
 * Key features:
 * - Connector dropdown. Selecting one shows that connector's actions
 * - Each timed action owns every / unit / local time / on-off. Save writes that action only
 * - Login verify has no scheduler
 * - Flags stay a list. A flag row opens a modal. On/off is the flag
 * - Cadence SoT is source_connector_actions. Flag save does not send cadence
 * - Utilization appears when its action row exists
 * - Utilization refresh nests under Utilization when both rows exist
 * - Flag on/off per SO only after an action path exists
 * - Flag toggle does not send cadence
 * - No shop password
 *
 * @dependencies adminApi or injected client
 *
 * @notes
 * - Display title may name the source app. Identifiers stay generic.
 * - A failed catalog load shows error, not Loading connectors… (LYN-394)
 * - Error with an existing list stays on the editor; do not replace it
 * - Flag toggles stay disabled until a successful flag load (LYN-394)
 * - Do not copy one cadence onto every timed action
 * - A connectors refresh keeps unsaved drafts. Server wins only for the
 *   saved header or the saved action
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

function cadenceFromAction(action) {
  return {
    intervalCount: action?.cadence?.intervalCount || 1,
    intervalUnit: action?.cadence?.intervalUnit || 'week',
    localTime: String(action?.cadence?.localTime || '00:01').slice(0, 5)
  };
}

function buildActionSave(action) {
  return {
    actionKey: action.actionKey,
    label: action.label,
    kind: action.kind,
    enabled: action.enabled !== false,
    cadence: action.kind === 'timed' ? action.cadence : null
  };
}

function draftFromConnector(connector) {
  return {
    title: connector.title || '',
    description: connector.description || '',
    logoUrl: connector.logoUrl || '',
    actions: (connector.actions || []).map((action) => ({
      ...action,
      cadence: action.kind === 'timed' ? cadenceFromAction(action) : null
    }))
  };
}

function actionDraftKey(action) {
  return JSON.stringify({
    enabled: action?.enabled !== false,
    cadence: action?.kind === 'timed' ? cadenceFromAction(action) : null
  });
}

const UTILIZATION_ACTION = 'utilization';
const UTILIZATION_REFRESH_ACTION = 'utilization_refresh';

function scheduledActionGroups(actions) {
  const list = actions || [];
  const refresh = list.find((action) => action.actionKey === UTILIZATION_REFRESH_ACTION);
  const utilizationExists = list.some((action) => action.actionKey === UTILIZATION_ACTION);
  return list
    .filter((action) => action.actionKey !== UTILIZATION_REFRESH_ACTION || !utilizationExists)
    .map((action) => ({
      action,
      nested: action.actionKey === UTILIZATION_ACTION && refresh ? [refresh] : []
    }));
}

function mergeConnectorDraft(prevDraft, prevBaseline, server, lastSave) {
  if (!prevDraft || !prevBaseline) return server;
  const takeHeader = lastSave?.kind === 'header';
  const savedActionKey = lastSave?.kind === 'action' ? lastSave.actionKey : null;
  const prevByKey = Object.fromEntries((prevDraft.actions || []).map((row) => [row.actionKey, row]));
  const baseByKey = Object.fromEntries((prevBaseline.actions || []).map((row) => [row.actionKey, row]));
  return {
    title: takeHeader || prevDraft.title === prevBaseline.title ? server.title : prevDraft.title,
    description: takeHeader || prevDraft.description === prevBaseline.description
      ? server.description
      : prevDraft.description,
    logoUrl: server.logoUrl,
    actions: (server.actions || []).map((serverAction) => {
      if (serverAction.actionKey === savedActionKey) return serverAction;
      const prev = prevByKey[serverAction.actionKey];
      const base = baseByKey[serverAction.actionKey];
      if (!prev || !base || actionDraftKey(prev) === actionDraftKey(base)) {
        return serverAction;
      }
      return {
        ...serverAction,
        enabled: prev.enabled,
        cadence: prev.cadence
      };
    })
  };
}

export default function SourceConnectorPanel({
  connectors,
  salesOrganizations,
  flagsByConnectorId,
  busy,
  error,
  onSaveCatalog,
  onSaveFlag,
  onUploadLogo
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [flagModal, setFlagModal] = useState(null);
  const baselinesRef = useRef({});
  const lastSaveRef = useRef(null);

  useEffect(() => {
    if (!connectors) return;
    const ids = new Set(connectors.map((row) => row.id));
    const prevBaselines = baselinesRef.current;
    const lastSave = lastSaveRef.current;
    lastSaveRef.current = null;
    const nextBaselines = {};
    for (const connector of connectors) {
      nextBaselines[connector.id] = draftFromConnector(connector);
    }
    setDrafts((prev) => {
      const next = {};
      for (const connector of connectors) {
        next[connector.id] = mergeConnectorDraft(
          prev[connector.id],
          prevBaselines[connector.id],
          nextBaselines[connector.id],
          lastSave?.connectorId === connector.id ? lastSave : null
        );
      }
      return next;
    });
    baselinesRef.current = nextBaselines;
    setSelectedId((prev) => {
      if (prev && ids.has(prev)) return prev;
      return connectors[0]?.id || null;
    });
  }, [connectors]);

  if (error && (!connectors || connectors.length === 0)) {
    return <p className="text-sm text-red-600">{error}</p>;
  }

  if (!connectors) {
    return <p className="text-sm text-gray-500">Loading connectors…</p>;
  }

  if (!connectors.length) {
    return <p className="text-sm text-gray-500">No titled connectors yet.</p>;
  }

  const connector = connectors.find((row) => row.id === selectedId) || connectors[0];
  const draft = drafts[connector.id] || {};
  const flagsReady = flagsByConnectorId != null;
  const flags = flagsReady ? (flagsByConnectorId[connector.id] || []) : [];
  const flagMap = Object.fromEntries(flags.map((flag) => [flag.salesOrgId, flag]));

  const updateDraft = (patch) => {
    setDrafts((prev) => ({
      ...prev,
      [connector.id]: { ...prev[connector.id], ...patch }
    }));
  };

  const updateAction = (actionKey, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [connector.id]: {
        ...prev[connector.id],
        actions: (prev[connector.id]?.actions || []).map((item) => (
          item.actionKey === actionKey ? { ...item, ...patch } : item
        ))
      }
    }));
  };

  const updateActionCadence = (actionKey, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [connector.id]: {
        ...prev[connector.id],
        actions: (prev[connector.id]?.actions || []).map((item) => (
          item.actionKey === actionKey
            ? { ...item, cadence: { ...item.cadence, ...patch } }
            : item
        ))
      }
    }));
  };

  return (
    <div className="space-y-6" data-testid="source-connector-panel">
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      <section className="bg-white border border-gray-200 rounded-xl">
        <div className="px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-medium text-gray-900">Connectors</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Last save wins. Login verify has no scheduler. Each timed action has its own schedule.
          </p>
        </div>
        <div className="px-5 py-4 space-y-3">
          <label className="block text-xs text-gray-500">
            Connector
            <select
              data-testid="source-connector-select"
              className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900"
              value={connector.id}
              onChange={(event) => setSelectedId(event.target.value)}
            >
              {connectors.map((row) => (
                <option key={row.id} value={row.id}>{row.title}</option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-gray-500">
            Title
            <input
              className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900"
              value={draft.title || ''}
              onChange={(event) => updateDraft({ title: event.target.value })}
            />
          </label>
          <label className="block text-xs text-gray-500">
            Logo
            <div className="mt-1 flex items-center gap-3">
              {draft.logoUrl ? (
                <img src={draft.logoUrl} alt="" className="h-10 w-10 rounded-md object-contain border border-gray-200" />
              ) : null}
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/x-icon"
                className="text-sm text-gray-900"
                data-testid="source-connector-logo-file"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file && onUploadLogo) onUploadLogo(connector.id, file);
                  event.target.value = '';
                }}
              />
            </div>
          </label>
          <label className="block text-xs text-gray-500">
            Description
            <textarea
              className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900"
              rows={2}
              value={draft.description || ''}
              onChange={(event) => updateDraft({ description: event.target.value })}
            />
          </label>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              lastSaveRef.current = { connectorId: connector.id, kind: 'header' };
              onSaveCatalog(connector.id, {
                title: draft.title,
                description: draft.description
              });
            }}
            className="inline-flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 rounded-md text-sm font-medium disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Save connector
          </button>
        </div>
        <div className="px-5 py-4 border-t border-gray-100 space-y-3">
          <h4 className="text-sm font-medium text-gray-900">Scheduled runs</h4>
          <p className="text-xs text-gray-500">
            Save writes that action only. Login verify has no scheduler.
          </p>
          <ul className="space-y-3">
            {scheduledActionGroups(draft.actions).map(({ action, nested }) => (
              <li
                key={action.actionKey}
                data-testid={`source-connector-action-${action.actionKey}`}
                className="rounded-lg border border-gray-100 p-3 space-y-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-gray-900">
                    {action.label}
                    {action.kind === 'verify' ? ' — no scheduler' : ''}
                  </span>
                  <label className="inline-flex items-center gap-2 text-xs text-gray-500">
                    On
                    <input
                      type="checkbox"
                      checked={action.enabled !== false}
                      onChange={(event) => updateAction(action.actionKey, {
                        enabled: event.target.checked
                      })}
                    />
                  </label>
                </div>
                {action.kind === 'timed' ? (
                  <div className="grid grid-cols-3 gap-3">
                    <label className="block text-xs text-gray-500">
                      Every
                      <input
                        type="number"
                        min={1}
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                        value={action.cadence?.intervalCount || 1}
                        onChange={(event) => updateActionCadence(action.actionKey, {
                          intervalCount: Number(event.target.value)
                        })}
                      />
                    </label>
                    <label className="block text-xs text-gray-500">
                      Unit
                      <select
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                        value={action.cadence?.intervalUnit || 'week'}
                        onChange={(event) => updateActionCadence(action.actionKey, {
                          intervalUnit: event.target.value
                        })}
                      >
                        <option value="hour">hour</option>
                        <option value="day">day</option>
                        <option value="week">week</option>
                        <option value="month">month</option>
                      </select>
                    </label>
                    <label className="block text-xs text-gray-500">
                      Local time
                      <input
                        type="time"
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                        value={action.cadence?.localTime || '00:01'}
                        onChange={(event) => updateActionCadence(action.actionKey, {
                          localTime: event.target.value
                        })}
                      />
                    </label>
                  </div>
                ) : null}
                <button
                  type="button"
                  disabled={busy}
                  data-testid={`source-connector-action-save-${action.actionKey}`}
                  onClick={() => {
                    lastSaveRef.current = {
                      connectorId: connector.id,
                      kind: 'action',
                      actionKey: action.actionKey
                    };
                    onSaveCatalog(connector.id, {
                      actions: [buildActionSave(action)]
                    });
                  }}
                  className="inline-flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 rounded-md text-sm font-medium disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  Save action
                </button>
                {nested.length ? (
                  <ul className="ml-4 pl-3 border-l border-gray-200 space-y-3">
                    {nested.map((child) => (
                      <li
                        key={child.actionKey}
                        data-testid={`source-connector-action-${child.actionKey}`}
                        className="rounded-lg border border-gray-100 p-3 space-y-3"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm text-gray-900">{child.label}</span>
                          <label className="inline-flex items-center gap-2 text-xs text-gray-500">
                            On
                            <input
                              type="checkbox"
                              checked={child.enabled !== false}
                              onChange={(event) => updateAction(child.actionKey, {
                                enabled: event.target.checked
                              })}
                            />
                          </label>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <label className="block text-xs text-gray-500">
                            Every
                            <input
                              type="number"
                              min={1}
                              className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                              value={child.cadence?.intervalCount || 1}
                              onChange={(event) => updateActionCadence(child.actionKey, {
                                intervalCount: Number(event.target.value)
                              })}
                            />
                          </label>
                          <label className="block text-xs text-gray-500">
                            Unit
                            <select
                              className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                              value={child.cadence?.intervalUnit || 'week'}
                              onChange={(event) => updateActionCadence(child.actionKey, {
                                intervalUnit: event.target.value
                              })}
                            >
                              <option value="hour">hour</option>
                              <option value="day">day</option>
                              <option value="week">week</option>
                              <option value="month">month</option>
                            </select>
                          </label>
                          <label className="block text-xs text-gray-500">
                            Local time
                            <input
                              type="time"
                              className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                              value={child.cadence?.localTime || '00:01'}
                              onChange={(event) => updateActionCadence(child.actionKey, {
                                localTime: event.target.value
                              })}
                            />
                          </label>
                        </div>
                        <button
                          type="button"
                          disabled={busy}
                          data-testid={`source-connector-action-save-${child.actionKey}`}
                          onClick={() => {
                            lastSaveRef.current = {
                              connectorId: connector.id,
                              kind: 'action',
                              actionKey: child.actionKey
                            };
                            onSaveCatalog(connector.id, {
                              actions: [buildActionSave(child)]
                            });
                          }}
                          className="inline-flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 rounded-md text-sm font-medium disabled:opacity-50"
                        >
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                          Save action
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
        <div className="px-5 py-4 border-t border-gray-100">
          <h4 className="text-sm font-medium text-gray-900">Connector Flags</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Flag on/off for a Sales Org. No shop password. Requires an action path.
          </p>
          {!flagsReady ? (
            <p className="text-xs text-gray-500 mt-1">Flags have not loaded.</p>
          ) : null}
          <ul className="mt-3 divide-y divide-gray-100">
            {(salesOrganizations || []).map((so) => {
              const flagged = flagsReady && flagMap[so.id]?.flagged === true;
              return (
                <li key={so.id} className="py-2 flex items-center justify-between gap-3">
                  <span className="text-sm text-gray-900">{so.organizationName}</span>
                  <button
                    type="button"
                    data-testid={`source-connector-flag-row-${so.id}`}
                    disabled={busy || !flagsReady || (!flagged && connector.canFlag === false)}
                    onClick={() => {
                      if (!flagsReady) return;
                      setFlagModal({
                        connectorId: connector.id,
                        salesOrgId: so.id,
                        organizationName: so.organizationName,
                        flagged
                      });
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                      !flagsReady ? 'bg-gray-100' : flagged ? 'bg-indigo-600' : 'bg-gray-200'
                    } disabled:opacity-50`}
                    aria-pressed={flagsReady ? flagged : undefined}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white ${
                      flagged ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
      {flagModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30"
          data-testid="source-connector-flag-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-4">
            <h4 className="text-sm font-semibold text-gray-900">{flagModal.organizationName}</h4>
            <p className="text-xs text-gray-500">On/off is the flag. Cadence stays on the action.</p>
            <label className="inline-flex items-center gap-2 text-sm text-gray-900">
              On
              <input
                type="checkbox"
                checked={flagModal.flagged === true}
                onChange={(event) => setFlagModal((prev) => ({
                  ...prev,
                  flagged: event.target.checked
                }))}
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="text-sm text-gray-600 px-3 py-1.5"
                onClick={() => setFlagModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                className="inline-flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 rounded-md text-sm font-medium disabled:opacity-50"
                onClick={() => {
                  onSaveFlag(flagModal.connectorId, {
                    salesOrgId: flagModal.salesOrgId,
                    flagged: flagModal.flagged
                  });
                  setFlagModal(null);
                }}
              >
                Save flag
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
